import { RefObject, useMemo } from 'react';

import type { BaseRowData, LegacyDataProvider } from '@adv-frontend/data-provider';
import { useHStack } from '@direct-frontend/components';
import type { Optional } from '@direct-frontend/stdlib';
import cn from 'classnames';

import type { ColumnCellWrapperTemplateProps, GridCell } from '../../../types';
import type { GridRowSelectionManager } from '../../../lib/selectionManager/selectionManager';

import styles from '../Grid.module.css';

/**
 * Пропсы компонента `Cell`.
 *
 * @typeParam RowData Данные грида.
 */
type CellProps<RowData extends BaseRowData> = {
    /**
     * Датапровайдер.
     */
    dataProvider: LegacyDataProvider<RowData>;
    /**
     * Менеджер выделения строк.
     */
    selectionManager: GridRowSelectionManager | undefined;
    /**
     * CSS-класс, который будет добавлен к контейнеру ячейки.
     */
    className?: string;
    /**
     * Инстанс ячейки грида.
     */
    cell: GridCell<RowData>;
    /**
     * Данные строки.
     */
    data: Optional<RowData>;
    /**
     * Ссылка на скролящуюся часть грида.
     */
    scrollableRef: RefObject<HTMLDivElement>;
    /**
     * Смещение ячейки.
     */
    offsetLeft?: number;
};

function defaultRenderCellWrapper({
    children,
    className,
    style,
    'data-testid': dataTestId,
}: ColumnCellWrapperTemplateProps) {
    return (
        <div className={className} style={style} data-testid={dataTestId}>
            {children}
        </div>
    );
}

/**
 * Компонент ячейки грида.
 *
 * @typeParam RowData Данные грида.
 */
export function Cell<RowData extends BaseRowData>({
    cell,
    className,
    data,
    dataProvider,
    selectionManager,
    scrollableRef,
    offsetLeft,
}: CellProps<RowData>) {
    const width = cell.column.getSize();
    const inlineStyles = useMemo(() => {
        // Если столбец виртуализирован, то используем абсолютное позиционирование
        if (offsetLeft !== undefined) {
            return {
                width,
                position: 'absolute' as const,
                left: offsetLeft,
            };
        }

        return { width };
    }, [width, offsetLeft]);
    const stackClasses = useHStack({});

    const renderCell = cell.column.columnDef.renderCell;
    const renderWrapper = cell.column.columnDef.renderCellWrapper ?? defaultRenderCellWrapper;
    const hasRightBorder = cell.column.columnDef.hasDelimiter;
    const cellContent =
        renderCell && data
            ? renderCell({ ...cell.getContext(), data, scrollableRef, dataProvider, selectionManager })
            : null;

    return renderWrapper({
        children: cellContent,
        className: cn(stackClasses, styles.cell, hasRightBorder ? styles.rightBorder : undefined, className),
        style: inlineStyles,
        'data-testid': `Grid.Cell-${cell.id}`,
    });
}

Cell.displayName = 'Cell';
